import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Loader2, FileText, CheckCircle, XCircle, Clock, ArrowUp, Trash2, Calendar as CalendarIcon, RotateCcw, Pencil } from "lucide-react";
import { ModificaRichiestaDialog } from "@/components/ModificaRichiestaDialog";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import SectionInfoBanner from "@/components/SectionInfoBanner";

// Helper function to format date from YYYY-MM-DD to DD-MM-YYYY (Italian format)
const formatDateIT = (dateStr) => {
  if (!dateStr) return "";
  try {
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

// Helper function to check if user has manager-level visibility
const isManagerRole = (ruolo) => ruolo === "titolare" || ruolo === "amministrativo";

// Ferie a ore add-on: weekday keys matching backend (Monday first)
const WEEKDAYS_IT = ["lunedi", "martedi", "mercoledi", "giovedi", "venerdi", "sabato", "domenica"];

// Ferie a ore: 24h time options (00:00 - 23:45, 15-min steps) to avoid AM/PM locale format
const TIME_OPTIONS_24H = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIME_OPTIONS_24H.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}

// Normalize keyboard input to HH:MM 24h (accepts "9", "930", "0930", "9:30"); returns null if invalid
const normalizeTime24 = (val) => {
  const digits = String(val || "").replace(/[^\d]/g, "");
  if (!digits) return "";
  let h, m;
  if (digits.length <= 2) { h = parseInt(digits, 10); m = 0; }
  else if (digits.length === 3) { h = parseInt(digits.slice(0, 1), 10); m = parseInt(digits.slice(1), 10); }
  else { h = parseInt(digits.slice(0, 2), 10); m = parseInt(digits.slice(2, 4), 10); }
  if (isNaN(h) || h > 23 || isNaN(m) || m > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const isValidTime24 = (val) => /^([01]\d|2[0-3]):[0-5]\d$/.test(val || "");

export default function RequestsPage() {
  const { user, token, settings } = useAuth();
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get("filter") || "all");
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [loadMoreCount, setLoadMoreCount] = useState(5);
  // Reject dialog state
  const [rejectTarget, setRejectTarget] = useState(null); // request being rejected
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  // Revoke dialog state (paid add-on: settings.revoca_enabled)
  const [revokeTarget, setRevokeTarget] = useState(null); // approved request being revoked
  const [revokeReason, setRevokeReason] = useState("");
  const [revoking, setRevoking] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // richiesta approvata da modificare (solo titolare, flag modifica_approvate_enabled)
  // Advance-rule confirmation dialog (only for titolare/amministrativo when bypassing rule)
  const [advanceWarnOpen, setAdvanceWarnOpen] = useState(false);
  const [advanceWarnMsg, setAdvanceWarnMsg] = useState("");
  const [pendingBypassPayload, setPendingBypassPayload] = useState(null);
  // Date range picker state for ferie (employee-side)
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [singleDate, setSingleDate] = useState(undefined);
  const [newRequest, setNewRequest] = useState({
    tipo: "ferie",
    data_inizio: "",
    data_fine: "",
    ora_inizio: "",
    ora_fine: "",
    note: ""
  });

  // Ferie a ore add-on (per-company paid feature)
  const ferieAOre = settings?.ferie_a_ore_enabled === true;

  // Live preview of net hours (lunch break deducted) for hourly vacation requests
  const computeOrePreview = () => {
    if (!singleDate || !newRequest.ora_inizio || !newRequest.ora_fine) return null;
    if (!isValidTime24(newRequest.ora_inizio) || !isValidTime24(newRequest.ora_fine)) return null;
    const [sh, sm] = newRequest.ora_inizio.split(":").map(Number);
    const [eh, em] = newRequest.ora_fine.split(":").map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    if (end <= start) return { error: "L'ora di fine deve essere successiva all'ora di inizio" };
    const dayIdx = (singleDate.getDay() + 6) % 7;
    const cfg = settings?.pausa_pranzo?.[WEEKDAYS_IT[dayIdx]] || {};
    let pausa = 0;
    if (cfg.attiva && cfg.dalle && cfg.alle) {
      const [ph, pm] = cfg.dalle.split(":").map(Number);
      const [qh, qm] = cfg.alle.split(":").map(Number);
      pausa = Math.max(0, Math.min(end, qh * 60 + qm) - Math.max(start, ph * 60 + pm));
    }
    const ore = Math.round(((end - start - pausa) / 60) * 100) / 100;
    return { ore, pausa: Math.round((pausa / 60) * 100) / 100 };
  };

  useEffect(() => {
    fetchRequests();
    // For dipendente/amministrativo on Enterprise (or with ferie a ore add-on), fetch own leave balance
    if (
      (user?.ruolo === "dipendente" || user?.ruolo === "amministrativo") &&
      (settings?.piano === "enterprise" || settings?.ferie_a_ore_enabled) &&
      settings?.mostra_saldi_dipendenti !== false
    ) {
      axios.get(`${API}/users/${user.id}/leave-balance`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then((res) => setLeaveBalance(res.data)).catch(() => {});
    }
  }, [token, user?.id, user?.ruolo, settings?.piano, settings?.ferie_a_ore_enabled, settings?.mostra_saldi_dipendenti]);

  // Update filter when URL params change
  useEffect(() => {
    const urlFilter = searchParams.get("filter");
    if (urlFilter) {
      setFilter(urlFilter);
    }
  }, [searchParams]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/leave-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
      setVisibleCount(5); // Reset visible count when fetching new data
    } catch (error) {
      toast.error("Errore nel caricamento delle richieste");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    // For ferie, validate date range
    if (newRequest.tipo === "ferie" && !ferieAOre) {
      if (!dateRange.from) {
        toast.error("Seleziona la data di inizio delle ferie");
        return;
      }
    } else {
      if (!singleDate) {
        toast.error(newRequest.tipo === "ferie" ? "Seleziona il giorno delle ferie" : "Seleziona la data del permesso");
        return;
      }
      if (newRequest.tipo === "ferie" && ferieAOre) {
        if (!newRequest.ora_inizio || !newRequest.ora_fine) {
          toast.error("Indica l'ora di inizio e l'ora di fine delle ferie");
          return;
        }
        if (!isValidTime24(newRequest.ora_inizio) || !isValidTime24(newRequest.ora_fine)) {
          toast.error("Formato ora non valido: usa il formato 24 ore HH:MM (es. 09:30)");
          return;
        }
        const preview = computeOrePreview();
        if (preview?.error) {
          toast.error(preview.error);
          return;
        }
        if (preview && preview.ore <= 0) {
          toast.error("L'intervallo selezionato coincide interamente con la pausa pranzo: nessuna ora da conteggiare");
          return;
        }
      }
    }

    // Build base payload
    let payload = { ...newRequest };
    if (newRequest.tipo === "ferie" && !ferieAOre) {
      payload.data_inizio = format(dateRange.from, "yyyy-MM-dd");
      payload.data_fine = format(dateRange.to || dateRange.from, "yyyy-MM-dd");
    } else {
      payload.data_inizio = format(singleDate, "yyyy-MM-dd");
      payload.data_fine = format(singleDate, "yyyy-MM-dd");
    }

    // Client-side check of "minimum advance days" rule for immediate feedback to dipendente
    const minAdvance = parseInt(settings?.min_advance_days_for_request || 0, 10) || 0;
    if (minAdvance > 0) {
      const startDate = new Date(payload.data_inizio + "T00:00:00");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysDiff = Math.floor((startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (daysDiff < minAdvance) {
        if (user?.ruolo === "dipendente") {
          toast.error(`Le richieste di Ferie e Permessi devono essere inserite almeno ${minAdvance} giorni prima della data di inizio. Mancano solo ${Math.max(daysDiff, 0)} giorni.`);
          return;
        }
        // Titolare/Amministrativo → ask for confirmation before bypassing
        setAdvanceWarnMsg(`La regola aziendale prevede ${minAdvance} giorni di preavviso, ma mancano solo ${Math.max(daysDiff, 0)} giorni. Vuoi inserire comunque la richiesta come eccezione?`);
        setPendingBypassPayload(payload);
        setAdvanceWarnOpen(true);
        return;
      }
    }

    await submitLeaveRequest(payload);
  };

  const submitLeaveRequest = async (payload) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leave-requests`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(payload.force_bypass_advance_rule
        ? "Richiesta inserita con eccezione alla regola di preavviso."
        : "Richiesta inviata con successo!");
      setNewRequestOpen(false);
      setAdvanceWarnOpen(false);
      setPendingBypassPayload(null);
      setNewRequest({ tipo: "ferie", data_inizio: "", data_fine: "", ora_inizio: "", ora_fine: "", note: "" });
      setDateRange({ from: undefined, to: undefined });
      setSingleDate(undefined);
      fetchRequests();
    } catch (error) {
      const detail = error.response?.data?.detail || "Errore nell'invio della richiesta";
      // Backend returned 409 with "ADVANCE_RULE_VIOLATION:" prefix → show our confirmation dialog
      if (typeof detail === "string" && detail.startsWith("ADVANCE_RULE_VIOLATION:")) {
        setAdvanceWarnMsg(detail.replace("ADVANCE_RULE_VIOLATION:", ""));
        setPendingBypassPayload(payload);
        setAdvanceWarnOpen(true);
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmBypassAdvance = async () => {
    if (!pendingBypassPayload) return;
    await submitLeaveRequest({ ...pendingBypassPayload, force_bypass_advance_rule: true });
  };

  const handleUpdateRequest = async (requestId, stato, note = "") => {
    try {
      await axios.put(`${API}/leave-requests/${requestId}`, { stato, note_risposta: note }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Richiesta ${stato === "approvato" ? "approvata" : "rifiutata"}!`);
      fetchRequests();
    } catch (error) {
      toast.error("Errore nell'aggiornamento della richiesta");
    }
  };

  const openRejectDialog = (request) => {
    setRejectTarget(request);
    setRejectReason("");
  };

  const handleConfirmReject = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await axios.put(`${API}/leave-requests/${rejectTarget.id}`, { stato: "rifiutato", note_risposta: rejectReason.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Richiesta rifiutata!");
      setRejectTarget(null);
      setRejectReason("");
      fetchRequests();
    } catch (error) {
      toast.error("Errore nell'aggiornamento della richiesta");
    } finally {
      setRejecting(false);
    }
  };


  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await axios.post(`${API}/leave-requests/${revokeTarget.id}/revoca`, { nota: revokeReason.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Approvazione revocata. Il dipendente è stato avvisato.");
      setRevokeTarget(null);
      setRevokeReason("");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella revoca della richiesta");
    } finally {
      setRevoking(false);
    }
  };


  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Sei sicuro di voler cancellare questa richiesta?")) {
      return;
    }
    try {
      await axios.delete(`${API}/leave-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Richiesta cancellata con successo!");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella cancellazione della richiesta");
    }
  };

  const filteredRequests = requests.filter((r) => {
    if (filter === "all") return true;
    return r.stato === filter;
  });

  const visibleRequests = filteredRequests.slice(0, visibleCount);
  const remainingCount = filteredRequests.length - visibleCount;

  const handleLoadMore = () => {
    const newCount = Math.min(visibleCount + loadMoreCount, filteredRequests.length);
    setVisibleCount(newCount);
  };

  // Reset visible count when filter changes
  useEffect(() => {
    setVisibleCount(5);
  }, [filter]);

  const getStatusBadge = (stato) => {
    switch (stato) {
      case "in_attesa":
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><Clock className="h-3 w-3 mr-1" />In attesa</Badge>;
      case "approvato":
        return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle className="h-3 w-3 mr-1" />Approvato</Badge>;
      case "rifiutato":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle className="h-3 w-3 mr-1" />Rifiutato</Badge>;
      case "revocato":
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="badge-revocato"><RotateCcw className="h-3 w-3 mr-1" />Revocata</Badge>;
      default:
        return <Badge>{stato}</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Section help banner */}
        <SectionInfoBanner
          sectionKey="richieste"
          title={user?.ruolo === "dipendente" ? "Cosa puoi fare nella Home" : "Cosa puoi fare in Richieste"}
          items={
            isManagerRole(user?.ruolo)
              ? [
                  "Visualizza tutte le richieste di ferie e permessi dei tuoi dipendenti",
                  "Filtra per stato: In attesa / Approvate / Rifiutate",
                  "Approva o rifiuta direttamente dalla lista con un click",
                  "Aggiungi una nota di risposta se vuoi spiegare il motivo della tua decisione",
                ]
              : [
                  "Visualizza lo storico delle tue richieste",
                  "Filtra per stato: Tutte / In attesa / Approvate / Rifiutate",
                  "Invia nuove richieste di ferie o permessi",
                  "Cancella le richieste in attesa se hai commesso errori e reinvia la richiesta corretta",
                ]
          }
        />
        {/* Header */}
        <div className="flex flex-col gap-4 items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" data-testid="requests-title">
              {user?.ruolo === "dipendente" ? "Home" : isManagerRole(user?.ruolo) ? "Tutte le Richieste" : "Le Mie Richieste"}
            </h1>
            {/* Leave balance prospect for dipendente/amministrativo on Enterprise — between title and subtitle */}
            {leaveBalance && (
              <Card className="border-slate-200 mt-4 mb-3" data-testid="dip-leave-balance-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5 text-theme-600" />
                    Le tue ferie e permessi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`grid grid-cols-1 ${leaveBalance.mostra_permessi_dipendente !== false ? 'md:grid-cols-2' : ''} gap-4`}>
                    {/* Ferie Card */}
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <h3 className="text-sm font-semibold text-emerald-800 mb-2 uppercase tracking-wider">{ferieAOre ? "Ferie (ore)" : "Ferie (giorni)"}</h3>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <p className="text-slate-500">Totali</p>
                          <p className="font-bold text-slate-900 text-lg">{ferieAOre ? (leaveBalance.ferie_ore_totali_annue || 0) + (leaveBalance.ferie_ore_riporto || 0) : leaveBalance.ferie_totali_annue + leaveBalance.ferie_riporto}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Usate</p>
                          <p className="font-bold text-slate-900 text-lg">{ferieAOre ? ((leaveBalance.ferie_ore_godute || 0) + (leaveBalance.ferie_ore_usate || 0)).toFixed(1) : leaveBalance.ferie_godute + leaveBalance.ferie_usate}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Disponibili</p>
                          {ferieAOre ? (
                            <p className={`font-bold text-lg ${(leaveBalance.ferie_ore_disponibili || 0) > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{leaveBalance.ferie_ore_disponibili || 0}</p>
                          ) : (
                            <p className={`font-bold text-lg ${leaveBalance.ferie_disponibili > 0 ? 'text-emerald-600' : 'text-red-600'}`}>{leaveBalance.ferie_disponibili}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Permessi Card — hidden when titolare disabled it for this employee */}
                    {leaveBalance.mostra_permessi_dipendente !== false && (
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <h3 className="text-sm font-semibold text-amber-800 mb-2 uppercase tracking-wider">Permessi (ore)</h3>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <p className="text-slate-500">Totali</p>
                            <p className="font-bold text-slate-900 text-lg">{leaveBalance.permessi_totali_annui + leaveBalance.permessi_riporto}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Usate</p>
                            <p className="font-bold text-slate-900 text-lg">{(leaveBalance.permessi_goduti + leaveBalance.permessi_usati).toFixed(1)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Disponibili</p>
                            <p className={`font-bold text-lg ${leaveBalance.permessi_disponibili > 0 ? 'text-amber-600' : 'text-red-600'}`}>{leaveBalance.permessi_disponibili}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            <p className="text-slate-500 mt-1">
              {user?.ruolo === "dipendente"
                ? "Invia una richiesta e visualizza lo stato"
                : isManagerRole(user?.ruolo)
                  ? "Visualizza le richieste dei dipendenti"
                  : "Visualizza e gestisci le tue richieste"}
            </p>
          </div>
          <div className={user?.ruolo === "dipendente" || (user?.ruolo === "amministrativo") ? "" : "hidden"}>
            <Dialog open={newRequestOpen} onOpenChange={setNewRequestOpen}>
              <DialogTrigger asChild>
                <Button className="btn-theme btn-press" data-testid="new-request-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Invia una richiesta di ferie o permesso
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="leave-request-dialog">
                <DialogHeader>
                  <DialogTitle>Nuova Richiesta</DialogTitle>
                  <DialogDescription>Compila i campi per inviare una richiesta.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateRequest} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={newRequest.tipo} onValueChange={(v) => {
                      setNewRequest({ ...newRequest, tipo: v, data_fine: v === "permesso" ? "" : newRequest.data_fine });
                      // Reset selectors when switching type
                      setDateRange({ from: undefined, to: undefined });
                      setSingleDate(undefined);
                    }}>
                      <SelectTrigger data-testid="request-tipo-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ferie">Ferie</SelectItem>
                        <SelectItem value="permesso">Permesso</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Date fields - calendar picker for ferie (range) and permesso (single). With ferie a ore add-on, ferie use single day + time range */}
                  {newRequest.tipo === "ferie" && !ferieAOre ? (
                    <div className="space-y-2">
                      <Label>Seleziona date</Label>
                      <p className="text-xs text-slate-500 mb-2">
                        Clicca su una data per selezionarla, poi clicca su un'altra data per selezionare un intervallo
                      </p>
                      <div className="border rounded-lg p-3 bg-slate-50">
                        <Calendar
                          mode="range"
                          selected={dateRange}
                          onSelect={(v) => setDateRange(v || { from: undefined, to: undefined })}
                          defaultMonth={new Date()}
                          locale={it}
                          numberOfMonths={1}
                          weekStartsOn={1}
                          className="mx-auto"
                          data-testid="request-date-picker"
                        />
                      </div>
                      {dateRange.from && !dateRange.to && (
                        <div className="flex items-center gap-2 text-sm bg-amber-50 text-amber-700 p-3 rounded-lg border border-amber-200 animate-pulse">
                          <CalendarIcon className="h-4 w-4 shrink-0" />
                          <span><strong>Adesso seleziona la data di fine ferie</strong></span>
                        </div>
                      )}
                      {dateRange.from && dateRange.to && (
                        <div className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 p-2 rounded-lg border border-emerald-200">
                          <CalendarIcon className="h-4 w-4 text-emerald-500" />
                          <span>
                            {format(dateRange.from, "dd MMM yyyy", { locale: it })}
                            {dateRange.to.getTime() !== dateRange.from.getTime() && (
                              <> → {format(dateRange.to, "dd MMM yyyy", { locale: it })}</>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>{newRequest.tipo === "ferie" ? "Seleziona il giorno di ferie" : "Seleziona la data del permesso"}</Label>
                        {newRequest.tipo === "ferie" && ferieAOre && (
                          <p className="text-xs text-slate-500">
                            Le ferie sono contabilizzate a ore: seleziona un solo giorno e indica l'orario di inizio e fine.
                          </p>
                        )}
                        <div className="border rounded-lg p-3 bg-slate-50">
                          <Calendar
                            mode="single"
                            selected={singleDate}
                            onSelect={setSingleDate}
                            defaultMonth={new Date()}
                            locale={it}
                            numberOfMonths={1}
                            weekStartsOn={1}
                            className="mx-auto"
                            data-testid="request-date-picker-single"
                          />
                        </div>
                        {singleDate && (
                          <div className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 p-2 rounded-lg border border-emerald-200">
                            <CalendarIcon className="h-4 w-4 text-emerald-500" />
                            <span>{format(singleDate, "dd MMM yyyy", { locale: it })}</span>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Dalle ore</Label>
                          {newRequest.tipo === "ferie" && ferieAOre ? (
                            <Input
                              type="text"
                              inputMode="numeric"
                              placeholder="es. 08:00"
                              maxLength={5}
                              list="time-options-24h"
                              value={newRequest.ora_inizio}
                              onChange={(e) => setNewRequest({ ...newRequest, ora_inizio: e.target.value })}
                              onBlur={(e) => {
                                const norm = normalizeTime24(e.target.value);
                                setNewRequest({ ...newRequest, ora_inizio: norm === null ? "" : norm });
                              }}
                              data-testid="request-ora-inizio"
                            />
                          ) : (
                            <Input
                              type="time"
                              value={newRequest.ora_inizio}
                              onChange={(e) => setNewRequest({ ...newRequest, ora_inizio: e.target.value })}
                              required
                              data-testid="request-ora-inizio"
                            />
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Alle ore</Label>
                          {newRequest.tipo === "ferie" && ferieAOre ? (
                            <Input
                              type="text"
                              inputMode="numeric"
                              placeholder="es. 17:00"
                              maxLength={5}
                              list="time-options-24h"
                              value={newRequest.ora_fine}
                              onChange={(e) => setNewRequest({ ...newRequest, ora_fine: e.target.value })}
                              onBlur={(e) => {
                                const norm = normalizeTime24(e.target.value);
                                setNewRequest({ ...newRequest, ora_fine: norm === null ? "" : norm });
                              }}
                              data-testid="request-ora-fine"
                            />
                          ) : (
                            <Input
                              type="time"
                              value={newRequest.ora_fine}
                              onChange={(e) => setNewRequest({ ...newRequest, ora_fine: e.target.value })}
                              required
                              data-testid="request-ora-fine"
                            />
                          )}
                        </div>
                        {newRequest.tipo === "ferie" && ferieAOre && (
                          <datalist id="time-options-24h">
                            {TIME_OPTIONS_24H.map((t) => (
                              <option key={t} value={t} />
                            ))}
                          </datalist>
                        )}
                      </div>
                      {/* Ferie a ore: live preview of computed hours with lunch break deduction */}
                      {newRequest.tipo === "ferie" && ferieAOre && (() => {
                        const preview = computeOrePreview();
                        if (!preview) return null;
                        if (preview.error) {
                          return (
                            <div className="text-sm bg-red-50 text-red-700 p-3 rounded-lg border border-red-200" data-testid="ferie-ore-preview-error">
                              {preview.error}
                            </div>
                          );
                        }
                        return (
                          <div className="text-sm bg-emerald-50 text-emerald-800 p-3 rounded-lg border border-emerald-200 space-y-1" data-testid="ferie-ore-preview">
                            <p><strong>Ore di ferie conteggiate: {preview.ore}</strong></p>
                            {preview.pausa > 0 && (
                              <p className="text-xs text-emerald-700">Pausa pranzo detratta automaticamente: {preview.pausa} {preview.pausa === 1 ? "ora" : "ore"}</p>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Note (opzionale)</Label>
                    <Textarea
                      placeholder="Aggiungi una nota..."
                      value={newRequest.note}
                      onChange={(e) => setNewRequest({ ...newRequest, note: e.target.value })}
                      data-testid="request-note"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-theme" disabled={submitting} data-testid="submit-request-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Invia Richiesta
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Tabs defaultValue="all" value={filter} onValueChange={setFilter}>
          <TabsList className="tabs-theme-list">
            <TabsTrigger value="all" className="tabs-theme-trigger" data-testid="filter-all">Tutte</TabsTrigger>
            <TabsTrigger value="in_attesa" className="tabs-theme-trigger" data-testid="filter-pending">In attesa</TabsTrigger>
            <TabsTrigger value="approvato" className="tabs-theme-trigger" data-testid="filter-approved">Approvate</TabsTrigger>
            <TabsTrigger value="rifiutato" className="tabs-theme-trigger" data-testid="filter-rejected">Rifiutate</TabsTrigger>
            {settings?.revoca_enabled && (
              <TabsTrigger value="revocato" className="tabs-theme-trigger" data-testid="filter-revoked">Revocate</TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        {/* Requests List */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {filteredRequests.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>Nessuna richiesta trovata</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {visibleRequests.map((request, index) => (
                  <div
                    key={request.id}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between p-5 gap-4 animate-fade-in hover:bg-slate-50 transition-colors`}
                    data-testid={`request-item-${request.id}`}
                  >
                    <div className="space-y-1 sm:flex-shrink-0">
                      {isManagerRole(user?.ruolo) && (
                        <p className="font-semibold text-slate-900">
                          {request.user_nome} {request.user_cognome}
                          {request.user_posizione && (
                            <span className="ml-2 text-sm font-normal text-slate-500">({request.user_posizione})</span>
                          )}
                        </p>
                      )}
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-slate-700">
                          {request.tipo === "ferie" ? "Ferie" : "Permesso"}
                        </span>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-600">
                          {request.tipo === "ferie" ? (
                            request.ora_inizio && request.ora_fine ? (
                              <>
                                {formatDateIT(request.data_inizio)} dalle {request.ora_inizio} alle {request.ora_fine}
                                {request.ore_calcolate != null && (
                                  <span className="ml-1 font-semibold text-emerald-700">({request.ore_calcolate} ore)</span>
                                )}
                              </>
                            ) : (
                              <>{formatDateIT(request.data_inizio)} → {formatDateIT(request.data_fine)}</>
                            )
                          ) : (
                            <>{formatDateIT(request.data_inizio)} {request.ora_inizio && request.ora_fine ? `dalle ${request.ora_inizio} alle ${request.ora_fine}` : ""}</>
                          )}
                        </span>
                      </div>
                      {request.note && (
                        <p className="text-sm text-slate-400 italic">"{request.note}"</p>
                      )}
                      {request.note_risposta && (
                        <p className="text-sm text-slate-500">Risposta: {request.note_risposta}</p>
                      )}
                    </div>
                    {/* Dipendente — horizontal row: badge + helper text + cancel button */}
                    {user?.ruolo === "dipendente" && request.user_id === user?.id && request.stato === "in_attesa" && (
                      <>
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          {getStatusBadge(request.stato)}
                          <p className="text-xs text-slate-400 italic">
                            Hai indicato date errate o non vuoi più che ti vengano approvate queste {request.tipo === "ferie" ? "ferie" : "permessi"}? Cancella la richiesta.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteRequest(request.id)}
                          className="border-red-200 text-red-600 hover:bg-red-50 btn-press shrink-0"
                          data-testid={`delete-btn-${request.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          <span>Cancella</span>
                        </Button>
                      </>
                    )}
                    {/* Dipendente — non-pending: just show the badge on the right */}
                    {user?.ruolo === "dipendente" && request.user_id === user?.id && request.stato !== "in_attesa" && (
                      <div className="flex items-center gap-3 flex-wrap ml-auto">
                        {getStatusBadge(request.stato)}
                      </div>
                    )}
                    {/* Manager view: badge + approve/reject buttons */}
                    {user?.ruolo !== "dipendente" && (
                      <div className="flex items-center gap-3 flex-wrap ml-auto">
                        {getStatusBadge(request.stato)}
                        {(user?.ruolo === "titolare" || (user?.ruolo === "amministrativo" && user?.permessi_admin?.gestione_ferie_permessi)) && request.stato === "in_attesa" && (
                          <div className="flex gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                            <Button
                              size="sm"
                              onClick={() => handleUpdateRequest(request.id, "approvato")}
                              className="bg-emerald-600 hover:bg-emerald-700 btn-press flex-1 sm:flex-none"
                              data-testid={`approve-btn-${request.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              <span>Approva</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openRejectDialog(request)}
                              className="border-red-200 text-red-600 hover:bg-red-50 btn-press flex-1 sm:flex-none"
                              data-testid={`reject-btn-${request.id}`}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              <span>Rifiuta</span>
                            </Button>
                          </div>
                        )}
                        {settings?.modifica_approvate_enabled && user?.ruolo === "titolare" && request.stato === "approvato" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditTarget(request)}
                            className="border-blue-300 text-blue-700 hover:bg-blue-50 btn-press"
                            title="Modifica date/orari di questa richiesta approvata"
                            data-testid={`edit-approved-btn-${request.id}`}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            <span>Modifica</span>
                          </Button>
                        )}
                        {settings?.revoca_enabled && (user?.ruolo === "titolare" || (user?.ruolo === "amministrativo" && user?.permessi_admin?.gestione_ferie_permessi)) && request.stato === "approvato" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setRevokeTarget(request); setRevokeReason(""); }}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50 btn-press"
                            data-testid={`revoke-btn-${request.id}`}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            <span>Revoca</span>
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Load More Section */}
                {remainingCount > 0 && (
                  <div className="flex items-center justify-center gap-3 p-4 bg-slate-50">
                    <span className="text-sm text-slate-500">Visualizza altre</span>
                    <Select
                      value={loadMoreCount.toString()}
                      onValueChange={(value) => setLoadMoreCount(parseInt(value))}
                    >
                      <SelectTrigger className="w-20 h-8" data-testid="load-more-select-requests">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 5, 10, 15, 20, 25, 50, 99].map((n) => (
                          <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLoadMore}
                      className="gap-2"
                      data-testid="load-more-btn-requests"
                    >
                      Carica ({remainingCount} rimanenti)
                    </Button>
                  </div>
                )}
                
                {/* Back to Top Button - shown when all items loaded and list is long */}
                {remainingCount <= 0 && visibleRequests.length > 5 && (
                  <div className="flex justify-center p-4 bg-slate-50">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="gap-2"
                      data-testid="back-to-top-btn-requests"
                    >
                      <ArrowUp className="h-4 w-4" />
                      Torna su
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reject reason dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Rifiuta richiesta</DialogTitle>
            <DialogDescription>
              Stai per rifiutare la richiesta di {rejectTarget?.user_nome ? `${rejectTarget.user_nome} ${rejectTarget.user_cognome || ""}` : "questo dipendente"}. Puoi indicare una motivazione (opzionale) — verrà mostrata al dipendente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Label htmlFor="reject-reason">Eventuale motivazione del rifiuto</Label>
            <Textarea
              id="reject-reason"
              placeholder="Es: in quel periodo siamo già scoperti..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              data-testid="reject-reason-input"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => { setRejectTarget(null); setRejectReason(""); }}
              disabled={rejecting}
              data-testid="reject-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={handleConfirmReject}
              disabled={rejecting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="reject-confirm-btn"
            >
              {rejecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Rifiuta richiesta
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke approved request dialog (paid add-on) */}
      {editTarget && <ModificaRichiestaDialog token={token} request={editTarget} onClose={() => setEditTarget(null)} onSaved={fetchRequests} />}

      <Dialog open={!!revokeTarget} onOpenChange={(open) => { if (!open) { setRevokeTarget(null); setRevokeReason(""); } }}>
        <DialogContent data-testid="revoke-dialog">
          <DialogHeader>
            <DialogTitle>Revoca approvazione</DialogTitle>
            <DialogDescription>
              Stai per revocare l'approvazione di {revokeTarget?.tipo === "ferie" ? "ferie" : "permesso"} di {revokeTarget?.user_nome ? `${revokeTarget.user_nome} ${revokeTarget.user_cognome || ""}` : "questo dipendente"} ({formatDateIT(revokeTarget?.data_inizio)} → {formatDateIT(revokeTarget?.data_fine)}). Il dipendente riceverà una notifica e i giorni/ore torneranno disponibili nel suo saldo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Label htmlFor="revoke-reason">Eventuale motivazione della revoca</Label>
            <Textarea
              id="revoke-reason"
              placeholder="Es: revocata su richiesta del dipendente per cambio date..."
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              rows={4}
              data-testid="revoke-reason-input"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => { setRevokeTarget(null); setRevokeReason(""); }}
              disabled={revoking}
              data-testid="revoke-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={handleConfirmRevoke}
              disabled={revoking}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="revoke-confirm-btn"
            >
              {revoking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Revoca approvazione
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Advance-rule bypass confirmation dialog (titolare/amministrativo only) */}
      <Dialog open={advanceWarnOpen} onOpenChange={(open) => { if (!open) { setAdvanceWarnOpen(false); setPendingBypassPayload(null); } }}>
        <DialogContent data-testid="advance-rule-bypass-dialog">
          <DialogHeader>
            <DialogTitle>Preavviso insufficiente</DialogTitle>
            <DialogDescription>
              {advanceWarnMsg}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
            La richiesta verrà comunque inserita e contrassegnata come <strong>eccezione alla regola di preavviso</strong> per essere tracciabile a posteriori.
          </div>
          <div className="flex gap-2 justify-end mt-2">
            <Button
              variant="outline"
              onClick={() => { setAdvanceWarnOpen(false); setPendingBypassPayload(null); }}
              disabled={submitting}
              data-testid="advance-rule-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={confirmBypassAdvance}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="advance-rule-confirm-btn"
            >
              {submitting ? "Inserimento..." : "Sì, inserisci comunque"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
